import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Vault, Key, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(r.data?.message || "Your password has been reset.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-foreground text-background overflow-hidden">
        <div className="flex items-center gap-2">
          <Vault size={28} weight="fill" />
          <span className="font-display font-black text-xl tracking-tight">CONTINUITY</span>
        </div>
        <div className="space-y-6">
          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter">
            Set a new<br />password.
          </h1>
          <p className="text-base text-background/70 max-w-md leading-relaxed">
            Choose a strong password you don't use anywhere else. This link works once and expires shortly.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs uppercase tracking-[0.2em] text-background/50">
          <div><ShieldCheck size={20} className="mb-2" /> Secure</div>
          <div><Key size={20} className="mb-2" /> Single-use</div>
          <div><Vault size={20} className="mb-2" /> Encrypted</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-7 animate-fade-up" data-testid="reset-password-panel">
          {!token ? (
            <div className="space-y-4">
              <h2 className="font-display text-3xl font-black tracking-tight">Invalid link</h2>
              <p className="text-sm text-muted-foreground">
                This reset link is missing its token. Please request a new one from the sign-in page.
              </p>
              <Button data-testid="goto-login-button" onClick={() => navigate("/login")} className="w-full h-11 font-semibold rounded-sm">
                Back to sign in
              </Button>
            </div>
          ) : done ? (
            <div className="space-y-5 text-center">
              <CheckCircle size={48} weight="fill" className="mx-auto text-[hsl(var(--accent))]" />
              <h2 className="font-display text-3xl font-black tracking-tight">Password reset</h2>
              <p className="text-sm text-muted-foreground">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Button data-testid="goto-login-button" onClick={() => navigate("/login")} className="w-full h-11 font-semibold rounded-sm">
                Go to sign in
              </Button>
            </div>
          ) : (
            <>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-3">Account recovery</p>
                <h2 className="font-display text-3xl font-black tracking-tight">Choose a new password</h2>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">New password</Label>
                  <Input data-testid="new-password-input" type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1.5"
                    placeholder="At least 8 characters" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Confirm password</Label>
                  <Input data-testid="confirm-password-input" type="password" required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} className="rounded-sm mt-1.5"
                    placeholder="Re-enter password" />
                </div>
                <Button data-testid="reset-submit-button" type="submit" disabled={busy} className="w-full h-11 font-semibold rounded-sm">
                  {busy ? "Resetting…" : "Reset password"}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground text-center">
                <button data-testid="back-to-signin" onClick={() => navigate("/login")} className="font-semibold text-foreground hover:underline">
                  Back to sign in
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
